import { areaInfo, areaName, DEFAULT_AREAS } from '@ftthnms/shared';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  loading?: boolean;
  block?: boolean;
}

/** fn-btn. One primary per view; loading shows a spinner and disables the button. */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading,
  block,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'fn-btn',
        `fn-btn--${variant}`,
        size !== 'md' && `fn-btn--${size}`,
        block && 'fn-btn--block',
        className,
      )}
    >
      {loading ? <span className="fn-spinner" aria-hidden /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: 'default' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  pressed?: boolean;
}

/** fn-icon-btn. `label` becomes aria-label and tooltip. */
export function IconButton({
  label,
  variant = 'default',
  size = 'md',
  pressed,
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      {...rest}
      className={cx(
        'fn-icon-btn',
        variant !== 'default' && `fn-icon-btn--${variant}`,
        size === 'sm' && 'fn-icon-btn--sm',
        className,
      )}
    >
      {children}
    </button>
  );
}

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

export function Badge({
  tone = 'neutral',
  dot,
  square,
  live,
  children,
}: {
  tone?: BadgeTone;
  dot?: boolean;
  square?: boolean;
  live?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'fn-badge',
        tone !== 'neutral' && `fn-badge--${tone}`,
        square && 'fn-badge--square',
        live && 'fn-badge--live',
      )}
    >
      {dot && <span className="fn-badge__dot" />}
      {children}
    </span>
  );
}

/**
 * Colour of an area: the theme's `region-*` token for the seeded areas (tuned for dark and light)
 * unless an admin picked another colour in Settings, otherwise the stored colour.
 * `resolveToken` turns a token name into a value (default: a CSS `var()`; the map passes computed values).
 */
export function regionColor(
  code: string,
  resolveToken: (name: string) => string = (n) => `var(--${n})`,
): string {
  const info = areaInfo(code);
  const seeded = DEFAULT_AREAS.find((a) => a.code === code);
  if (seeded && (!info?.color || info.color.toLowerCase() === seeded.color?.toLowerCase()))
    return resolveToken(`region-${code}`);
  return info?.color ?? resolveToken('text-muted');
}

/** Region chip with the `region-*` swatch. Always shows the name, never colour alone. */
export function RegionChip({ code, label }: { code: string | 'all'; label?: string }) {
  const style = code === 'all' ? undefined : ({ '--region': regionColor(code) } as CSSProperties);
  return (
    <span className={cx('fn-region', `fn-region--${code}`)} style={style}>
      {label ?? (code === 'all' ? 'Semua wilayah' : areaName(code))}
    </span>
  );
}

export type MonitorStatus = 'online' | 'warning' | 'offline' | 'maintenance' | 'unknown';
const STATUS_WORD: Record<MonitorStatus, string> = {
  online: 'Online',
  warning: 'Warning',
  offline: 'Offline',
  maintenance: 'Maintenance',
  unknown: 'Belum dicek',
};

export function StatusIndicator({ status, meta }: { status: MonitorStatus; meta?: ReactNode }) {
  return (
    <span className={`fn-status fn-status--${status}`}>
      <span className="fn-status__dot" />
      {STATUS_WORD[status]}
      {meta && <span className="fn-status__meta">{meta}</span>}
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  text?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="fn-empty">
      {icon && <div className="fn-empty__icon">{icon}</div>}
      <h3 className="fn-empty__title">{title}</h3>
      {text && <p className="fn-empty__text">{text}</p>}
      {actions && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>{actions}</div>
      )}
    </div>
  );
}

export { cx };
